import { FC, useMemo } from 'react';
import { IconTypes } from '../Icon/Icon-Constants.ts';
import { ButtonType } from './Button-Constants.ts';
import { PartnerType } from '@/moment/types/partner-types.ts';
import Icon from '../Icon/Icon.tsx';
import './Button.scss';

export interface ButtonProps {
  disabled?: boolean;
  type?: ButtonType;
  backgroundColor?: string;
  textColor?: string;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  label?: string;
  icon?: IconTypes;
  partner?: PartnerType;
  outline?: boolean;
  textOnly?: boolean;
  fullWidth?: boolean;
  onClick?: () => void;
}

/** Primary UI component for user interaction */
export const Button: FC<ButtonProps> = ({
  type = ButtonType.Primary,
  size = 'md',
  backgroundColor,
  textColor,
  className,
  label,
  disabled = false,
  icon,
  partner = PartnerType.CRICKET, 
  outline = false,
  textOnly = false,
  fullWidth = false,
  ...props
}: ButtonProps) => {

  const mode = useMemo(() => {
    switch(type) {
      case ButtonType.Primary:
        return 'button--primary';
      case ButtonType.Secondary:
        return 'button--secondary';
      default:
        return 'button--primary';
    }
  }, [type]);

  const partnerClass = useMemo(() => {
    switch(partner) {
      case PartnerType.CRICKET:
        return 'button--primary-cricket';
      case PartnerType.DT:
        return 'button--primary-dt';
      case PartnerType.NEWSPOP:
          return 'button--primary-newspop';
      case PartnerType.VERIZON:
        return 'button--primary-verizon';
      default:
        return 'button--primary-cricket';
    }
  }, [partner]);

  const sizeClass = useMemo(() => {
    switch(size) {
      case 'lg': 
        return 'button-lg button-text-lg';
      case 'md':
        return 'button-md button-text-md';
      case 'sm':
        return 'button-sm button-text-sm';
      default:
        return 'button-md button-text-md';
    }
  }, [size])

  const classList = useMemo(() => {
    return [
      'button',
      'button-text-md',
      outline ? 'button-outline' : '',
      textOnly ? 'button-text-only' : '',
      mode,
      className,
      disabled ? 'button--disabled' : '',
      icon ? 'button--icon' : '',
      fullWidth ? 'button--full-width' : '',
      partnerClass,
      sizeClass
    ].join(' ');
  }, [mode, className, sizeClass, disabled, icon, partnerClass, outline, textOnly, fullWidth]);

  const styleList = useMemo(() => {
    return { backgroundColor, color: textColor };
  }, [backgroundColor, textColor]);

  return (
    <button
      disabled={disabled}
      type="button"
      className={classList}
      style={styleList}
      {...props}
    >
        { icon ? <Icon name={icon} /> : label }
    </button>
  );
};
